// packages/ui-kit/src/primitives/Page.tsx
// C3F v18 — reference: design/system/components/navigation/Page.{jsx,prompt.md}
import type { ReactNode } from "react";
import { cn } from "../lib/cn.js";

/**
 * Page-level layout primitives.
 *
 * They exist because the "how wide is this screen" decision used to be
 * retaken from scratch in every view, so it diverged: eight different
 * constraints plus three views with none. The rule they encode: a container
 * exists for the readability of text, not for decoration. Where the content
 * is a grid of data, capping the width removes information and gives nothing
 * back.
 *
 * The generous bottom gutter (96px desktop / 104px mobile) is not breathing
 * room: it is the clearance for the floating assistant button that covers the
 * bottom-right corner (mobile adds the fixed bottom-nav underneath). It lives
 * here and not on the scroll container because full-height screens must be
 * able to reach the edge.
 */

const PAGE_X = "px-4 sm:px-6";
/** Bottom gutter for the floating assistant button (+ mobile bottom nav). */
const PAGE_B = "pb-[104px] md:pb-24";

interface PageShellProps {
  /** Mono kicker over the title. Rendered ONLY in density="editorial". */
  readonly eyebrow?: string;
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  readonly actions?: ReactNode;
  readonly tabs?: ReactNode;
  readonly children?: ReactNode;
  readonly maxWidth?: number;
  /**
   * "editorial" (34px + eyebrow + 18px lede) only where the title is spoken
   * **in voice** — «Buongiorno, Marta.», «Vedi dove serve agire.». Default
   * "compact" (24px, no eyebrow, 14px subtitle) where the title is a label —
   * «Notifiche», «Struttura e soglie» — because there the sub-bar already
   * names the page. The criterion is NOT "area landing": «Pianificazione
   * settimanale» is a landing, but it stays a label.
   */
  readonly density?: "compact" | "editorial";
  readonly className?: string;
}

/**
 * Page shell: header (eyebrow/title/description/actions), tabs, content.
 * Every standard view starts here.
 */
export function Page({
  eyebrow,
  title,
  description,
  actions,
  tabs,
  children,
  maxWidth = 1120,
  density = "compact",
  className,
}: PageShellProps) {
  const ed = density === "editorial";
  return (
    <div
      data-testid="page"
      style={{ maxWidth }}
      className={cn(
        "mx-auto grid w-full",
        PAGE_X,
        PAGE_B,
        ed ? "gap-6 pt-7" : "gap-5 pt-5",
        className,
      )}
    >
      {(title || actions) && (
        <header className="flex flex-wrap items-end justify-between gap-5">
          {/* flex: 1 1 320px + min-w-0 — a column sized by its content wraps
              badly (build.md): the box ends exactly at the title's natural
              width and the second line lands on the next paragraph. */}
          <div className={cn("grid min-w-0 flex-[1_1_320px]", ed ? "gap-[7px]" : "gap-[3px]")}>
            {ed && eyebrow && <div className="text-eyebrow">{eyebrow}</div>}
            {title && (
              <h1
                className={cn(
                  "m-0 [text-wrap:balance]",
                  ed ? "text-h1 font-bold" : "text-h3 font-semibold",
                )}
              >
                {title}
              </h1>
            )}
            {description && (
              <p className={cn("m-0 max-w-[62ch] text-fg-muted", ed ? "text-lede" : "text-sm")}>
                {description}
              </p>
            )}
          </div>
          {actions && <div className="flex shrink-0 gap-2.5">{actions}</div>}
        </header>
      )}
      {tabs}
      <div className="grid gap-5">{children}</div>
    </div>
  );
}

interface PageProps {
  readonly children: ReactNode;
  readonly className?: string;
}

/**
 * Dense surfaces: grids, boards, lists. No reading cap — only an ultrawide
 * ceiling, past which the eye has to cross the whole screen to connect the
 * first column to the last.
 */
export function PageFull({ children, className }: PageProps) {
  return (
    <div
      data-testid="page-full"
      className={cn("mx-auto w-full max-w-[1800px]", PAGE_X, "pt-4 sm:pt-6", PAGE_B, className)}
    >
      {children}
    </div>
  );
}

/**
 * A block INSIDE a PageFull, for pages that are mixed: a stat grid gains from
 * the width (more departments per row) while the list under it loses (a
 * progress bar a metre and a half wide no longer reads at a glance).
 */
export function Section({
  children,
  width = "full",
  className,
}: PageProps & { readonly width?: "full" | "readable" }) {
  return <div className={cn(width === "readable" && "max-w-3xl", className)}>{children}</div>;
}
